import React, { useState, useEffect } from 'react';
import { useGetComponentContentsQuery } from '../../app/services/componentContentService';
import {
    Type,
    Image,
    PlayCircle,
    Calendar,
    Clock,
    Cloud,
    FileText,
    Monitor
} from 'lucide-react';
import { GetDefaultSize } from '../../common/constants';
import { EnumScreenOrientation } from '../../app/@types/template';
import TextPreviewer from './TextPreviewer';
import ImagePreviewer from './ImagePreviewer';
import VideoPreviewer from './VideoPreviewer';
import MarqueePreviewer from './MarqueePreviewer';
import DatePreviewer from './DatePreviewer';
import TimePreviewer from './TimePreviewer';
import RichTextPreviewer from './RichTextPreviewer';
import WeatherPreviewer from './WeatherPreviewer';
import FilePreviewer from './FilePreviewer';
import LiveVideoPreviewer from './LiveVideoPreviewer';

interface ComponentPreviewProps {
    component: Frame.Component;
    screenFrameConfigId: number;
    orientation: EnumScreenOrientation;
}

const ComponentPreview: React.FC<ComponentPreviewProps> = ({
    component,
    screenFrameConfigId,
    orientation
}) => {
    const [currentContentIndex, setCurrentContentIndex] = useState(0);

    const {
        data: contentData,
        isLoading,
        isError
    } = useGetComponentContentsQuery({
        componentId: parseInt(component.id?.toString() || '0'),
        screenFrameConfigId: screenFrameConfigId
    }, {
        skip: !component.id
    });

    const contents = contentData?.data || [];
    const currentContent = contents[currentContentIndex];

    useEffect(() => {
        if (!currentContent || contents.length <= 1) return;

        const duration = currentContent.duration || 5;
        const timer = setTimeout(() => {
            setCurrentContentIndex(prev => (prev + 1) % contents.length);
        }, duration * 1000);

        return () => clearTimeout(timer);
    }, [currentContentIndex, currentContent, contents.length]);

    const getComponentIcon = (type: Frame.EnumComponentType) => {
        switch (type) {
            case 'Text':
                return <Type className="h-4 w-4" />;
            case 'Image':
                return <Image className="h-4 w-4" />;
            case 'Video':
                return <PlayCircle className="h-4 w-4" />;
            case 'Marquee':
                return <Type className="h-4 w-4" />;
            case 'Date':
                return <Calendar className="h-4 w-4" />;
            case 'Time':
                return <Clock className="h-4 w-4" />;
            case 'Weather':
                return <Cloud className="h-4 w-4" />;
            case 'File':
                return <FileText className="h-4 w-4" />;
            default:
                return <Monitor className="h-4 w-4" />;
        }
    };

    const getComponentStyles = () => {
        return {
            position: 'absolute' as const,
            left: `${(component.x / GetDefaultSize(orientation).width) * 100}%`,
            top: `${(component.y / GetDefaultSize(orientation).height) * 100}%`,
            width: `${(component.width / GetDefaultSize(orientation).width) * 100}%`,
            height: `${(component.height / GetDefaultSize(orientation).height) * 100}%`,
            backgroundColor: component.backgroundColor || '#ffffff',
            overflow: 'hidden',
            boxSizing: 'border-box' as const,
        };
    };

    const renderPlaceholder = () => {
        return (
            <div 
                className="w-full h-full flex items-center justify-center text-xs"
                style={{
                    color: component.color || '#666',
                    border: component.borderThickness 
                        ? `${component.borderThickness}px solid ${component.borderColor || '#ccc'}`
                        : 'none',
                    borderRadius: component.borderRadius ? `${component.borderRadius}px` : '0',
                }}
            >
                <div className="flex items-center gap-1">
                    {getComponentIcon(component.type)}
                    <span>{component.placeholder || component.type}</span>
                </div>
            </div>
        );
    };

    const renderComponentContent = () => {
        if (isLoading) {
            return (
                <div className="w-full h-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                </div>
            );
        }

        // Show placeholder if no content
        if (isError || !currentContent || !currentContent.content) {
            return renderPlaceholder();
        }

        switch (component.type) {
            case 'Text':
                return (
                    <TextPreviewer 
                        content={currentContent.content} 
                        component={component}
                    />
                );

            case 'Marquee':
                return (
                    <MarqueePreviewer
                        content={currentContent.content}
                        direction={component.marqueeDirection}
                        scrollAmount={component.scrollAmount}
                    />
                );

            case 'Image':
                return <ImagePreviewer fileName={currentContent.content} />;

            case 'Video':
                return <VideoPreviewer fileName={currentContent.content} />;

            case 'Date':
                return <DatePreviewer />;

            case 'Time':
                return <TimePreviewer />;

            case 'RichText':
                return <RichTextPreviewer htmlContent={currentContent.content} />;

            case 'Weather':
                return <WeatherPreviewer />;

            case 'File':
                return <FilePreviewer fileName={currentContent.content} />;

            case 'LiveVideo':
                return <LiveVideoPreviewer videoUrl={currentContent.content} />;

            default:
                return renderPlaceholder();
        }
    };

    return (
        <div style={getComponentStyles()}>
            {renderComponentContent()}
        </div>
    );
};

export default ComponentPreview;
